import { useEffect, lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import SiteLayout from './components/SiteLayout.jsx';
import HomePage from './pages/HomePage.jsx';
import ContentPage from './pages/ContentPage.jsx';
import FlightsPage from './pages/FlightsPage.jsx';
import EventsPage from './pages/EventsPage.jsx';
import GalleryPage from './pages/GalleryPage.jsx';
import VipServicePage from './pages/VipServicePage.jsx';
import ContactPage from './pages/ContactPage.jsx';
import FaqsPage from './pages/FaqsPage.jsx';
import ComingSoon from './pages/ComingSoon.jsx';
import { PAGES, PAGE_REDIRECTS } from './data/pagesContent.js';

// Back Office (CMS) — lazy-loaded so it never affects the public bundle/site.
const AdminApp = lazy(() => import('./admin/AdminApp.jsx'));

function AdminFallback() {
  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: '#f4f6fb' }}>
      <div
        style={{
          width: 38,
          height: 38,
          borderRadius: '50%',
          border: '3px solid #dbeafe',
          borderTopColor: '#2563eb',
          animation: 'nkcSpin 0.7s linear infinite',
        }}
      />
      <style>{'@keyframes nkcSpin{to{transform:rotate(360deg)}}'}</style>
    </div>
  );
}

function BodyClassManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    // Back office manages its own full-screen layout — clear all public classes.
    if (pathname.startsWith('/backoffice')) {
      document.body.classList.remove(
        'home-v4',
        'inner-page',
        'inner-page--with-sidebar',
        'inner-page--flights',
        'inner-page--flights-board-only',
        'flights-page--light'
      );
      document.documentElement.classList.remove('v4-splash-pending');
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      return;
    }
    const isHome = pathname === '/' || pathname === '';
    const isFlights = pathname === '/flights';
    document.body.classList.toggle('home-v4', isHome);
    document.body.classList.toggle('inner-page', !isHome);
    document.body.classList.toggle('inner-page--with-sidebar', !isHome && !isFlights);
    document.body.classList.toggle('inner-page--flights', isFlights);
    document.body.classList.toggle('inner-page--flights-board-only', isFlights);
    document.body.classList.toggle('flights-page--light', isFlights);

    // Splash lock is homepage-only — clear so inner pages can scroll
    if (!isHome) {
      document.documentElement.classList.remove('v4-splash-pending');
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }

    if (isHome) {
      document.title = 'Nouakchott Oumtounsy International Airport (NKC/GQNO) | Home';
    }
  }, [pathname]);

  // New route → start at top (or jump to #hash once the page paints)
  useEffect(() => {
    if (hash) {
      const id = decodeURIComponent(hash.replace(/^#/, ''));
      const go = () => {
        const el = id ? document.getElementById(id) : null;
        if (el) {
          el.scrollIntoView({ behavior: 'auto', block: 'start' });
        } else {
          window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        }
      };
      requestAnimationFrame(() => requestAnimationFrame(go));
      return undefined;
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    return undefined;
  }, [pathname, hash]);

  return null;
}

const contentSlugs = Object.keys(PAGES).filter(
  (slug) =>
    slug !== 'events' &&
    slug !== 'gallery' &&
    slug !== 'contact' &&
    slug !== 'faqs' &&
    slug !== 'vip-service'
);

export default function App() {
  return (
    <>
      <BodyClassManager />
      <Routes>
        {/* Back Office (CMS) — separate from the public site, lazy-loaded */}
        <Route
          path="/backoffice/*"
          element={
            <Suspense fallback={<AdminFallback />}>
              <AdminApp />
            </Suspense>
          }
        />
        <Route element={<SiteLayout />}>
          <Route index element={<HomePage />} />
          <Route path="flights" element={<FlightsPage />} />
          <Route path="events" element={<EventsPage />} />
          <Route path="gallery" element={<GalleryPage />} />
          <Route path="vip-service" element={<VipServicePage />} />
          <Route path="contact" element={<ContactPage />} />
          <Route path="faqs" element={<FaqsPage />} />
          {Object.entries(PAGE_REDIRECTS).map(([from, to]) => (
            <Route key={from} path={from} element={<Navigate to={to} replace />} />
          ))}
          {contentSlugs.map((slug) => (
            <Route key={slug} path={slug} element={<ContentPage slug={slug} />} />
          ))}
          <Route path="*" element={<ComingSoon />} />
        </Route>
      </Routes>
    </>
  );
}
